import type { OnboardingLinkStatus, OutboxFilterStatus } from '@/shared/api';

/**
 * Deutsche Beschriftung der System-Flächen.
 *
 * <p>Grundregel der Guidelines §11 auf einer Forensikfläche: der Betreiber liest die fachliche
 * Bedeutung, die rohe Kennung bleibt daneben stehen, wo sie zum Suchen gebraucht wird. Deshalb
 * werden Status und Entitätsart übersetzt, die Vorgangsnamen des Änderungsprotokolls dagegen
 * nicht — sie sind freie Zeichenketten aus dem Backend (`AuditLogService.LogAsync`), es gibt
 * mehrere Dutzend davon, und genau nach ihrem Wortlaut wird gefiltert.</p>
 */

const AUDIT_ENTITY_LABEL: Record<string, string> = {
  All: 'Alle Bereiche',
  Property: 'Apartment',
  PropertyGroup: 'Objekt',
  Booking: 'Buchung',
  GuestProfile: 'Gastprofil',
  CleaningTask: 'Reinigungsauftrag',
  DamageReport: 'Schadensmeldung',
  PropertyFile: 'Datei in der Akte',
  OnboardingLink: 'Gastzugang',
  OutboxMessage: 'Ausgehende Mail',
  ApplicationUser: 'Benutzerkonto',
  Company: 'Firma',
  PropertyAssignment: 'Apartment-Zuweisung',
  Tenant: 'Workspace',
};

export function auditEntityLabel(entityType: string): string {
  return AUDIT_ENTITY_LABEL[entityType] ?? entityType;
}

/**
 * Deutsche Bedeutung eines Vorgangsnamens.
 *
 * <p>Im Änderungsprotokoll selbst bleibt der Rohwert stehen — dort ist er die Filtergrundlage
 * (D3-Entscheid). In der Zeitleiste eines Kontos ist er dagegen der <em>einzige</em> Inhalt der
 * Zeile; „RefreshRotated" beantwortet dort keine Frage. Deshalb hier eine ausdrückliche
 * Zuordnung der bekannten Vorgänge; alles Unbekannte fällt auf den Rohwert zurück, damit nie
 * ein Ereignis behauptet wird, das der Vertrag nicht liefert.</p>
 */
const AUDIT_ACTION_LABEL: Record<string, string> = {
  LoginSucceeded: 'Angemeldet',
  LoginFailed: 'Anmeldung fehlgeschlagen',
  RefreshRotated: 'Sitzung verlängert',
  LogoutSucceeded: 'Abgemeldet',
  UserCreated: 'Benutzer eingeladen',
  UserUpdated: 'Benutzer geändert',
  UserDeactivated: 'Zugang gesperrt',
  UserReactivated: 'Zugang freigegeben',
  UserRoleChanged: 'Rolle geändert',
  UserInvitationResent: 'Einladung erneut versendet',
  WorkspaceAdminInvited: 'Als Administrator eingeladen',
  WorkspaceAdminInvitationResent: 'Einladung erneut versendet (StayOn)',
  UserTwoFactorReset: 'Zwei-Faktor zurückgesetzt',
  PlatformAdminGranted: 'Zum Plattform-Admin gemacht',
  PlatformAdminRevoked: 'Plattform-Admin entzogen',
  CompanyCreated: 'Firma angelegt',
  CompanyUpdated: 'Firma geändert',
  PropertyAssigned: 'Apartment zugewiesen',
  PropertyAssignmentRevoked: 'Zuweisung entzogen',
  PropertyMoved: 'Apartment verschoben',
  PropertyWholeFlatLinked: 'Zimmer mit Gesamtwohnung verknüpft',
  PropertyWholeFlatUnlinked: 'Zimmer von Gesamtwohnung gelöst',
  Created: 'Angelegt',
  Updated: 'Geändert',
  Deleted: 'Gelöscht',
};

export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABEL[action] ?? action;
}

const OUTBOX_STATUS_LABEL: Record<OutboxFilterStatus, string> = {
  All: 'Alle',
  Pending: 'Offen',
  Processed: 'Versendet',
  Failed: 'Gescheitert',
};

export function outboxStatusLabel(status: OutboxFilterStatus): string {
  return OUTBOX_STATUS_LABEL[status];
}

/**
 * Der Nachrichtentyp der Outbox kennt heute genau einen Wert (`OutboxMessageTypes.SendEmail`).
 * Er steht trotzdem hier, damit ein zweiter Typ nicht als Rohwert in der Tabelle auftaucht.
 */
const OUTBOX_TYPE_LABEL: Record<string, string> = {
  SendEmail: 'E-Mail',
};

export function outboxTypeLabel(type: string): string {
  return OUTBOX_TYPE_LABEL[type] ?? type;
}

const ONBOARDING_STATUS_LABEL: Record<OnboardingLinkStatus, string> = {
  Active: 'Gültig',
  Sent: 'Verschickt',
  Used: 'Benutzt',
  Expired: 'Abgelaufen',
};

export function onboardingStatusLabel(status: OnboardingLinkStatus): string {
  return ONBOARDING_STATUS_LABEL[status] ?? status;
}
